/**
 * Dimentry Landing Page - AI Consulting & Strategic Technology
 */

import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import {
  ArrowRight,
  Brain,
  Building,
  Cloud,
  Database,
  Heart,
  LogIn,
  ShoppingCart,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import '../../styles/dimentry-landing.css';

type InfoCard = {
  title: string;
  description: string;
  icon: LucideIcon;
  accentClass: string;
};

const expertiseCards: InfoCard[] = [
  {
    title: 'AI & Agentic Systems',
    description:
      'Custom copilots, autonomous workflows, and retrieval-first orchestration tuned to your operating model.',
    icon: Brain,
    accentClass: 'bg-chart-2/10 text-chart-2',
  },
  {
    title: 'Data Lakes & Warehousing',
    description:
      'Clean, governed, and query-ready data foundations that support executive reporting and model training.',
    icon: Database,
    accentClass: 'bg-primary/10 text-primary',
  },
  {
    title: 'Analytics & Decision Science',
    description:
      'Operational scorecards and predictive insight loops that move teams from guesswork to evidence-backed action.',
    icon: TrendingUp,
    accentClass: 'bg-chart-5/10 text-chart-5',
  },
  {
    title: 'Cloud Migration & DevOps',
    description:
      'Platform modernization with resilient pipelines, cost control, and release confidence built in from day one.',
    icon: Cloud,
    accentClass: 'bg-chart-2/10 text-chart-2',
  },
];

const industryCards: InfoCard[] = [
  {
    title: 'Financial Services',
    description: 'Compliance automation, risk signal pipelines, and faster decision cycles.',
    icon: TrendingUp,
    accentClass: 'bg-chart-2/10 text-chart-2',
  },
  {
    title: 'Healthcare Systems',
    description: 'Patient data harmonization and clinical workflow intelligence with auditability.',
    icon: Heart,
    accentClass: 'bg-destructive/10 text-destructive',
  },
  {
    title: 'Enterprise Engineering',
    description: 'Reliability programs powered by telemetry, planning analytics, and digital twins.',
    icon: Wrench,
    accentClass: 'bg-chart-5/10 text-chart-5',
  },
  {
    title: 'Infrastructure & Construction',
    description: 'Portfolio visibility, schedule risk alerts, and resource optimization.',
    icon: Building,
    accentClass: 'bg-chart-1/10 text-chart-1',
  },
  {
    title: 'Digital Commerce',
    description: 'Personalization engines, demand insight, and margin-focused growth operations.',
    icon: ShoppingCart,
    accentClass: 'bg-primary/10 text-primary',
  },
];

const performanceStats = [
  { value: '98%', label: 'Client satisfaction' },
  { value: '150+', label: 'Delivery programs launched' },
  { value: '5x', label: 'Typical ROI acceleration' },
];

const revealDelayClasses = ['dl-delay-1', 'dl-delay-2', 'dl-delay-3', 'dl-delay-4'];

export default function DimentryLanding() {
  const navigate = useNavigate();

  return (
    <div className="dimentry-landing min-h-screen">
      {/* Top Navigation Bar */}
      <nav className="dl-top-nav fixed left-0 right-0 top-0 z-50 border-b backdrop-blur-md">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 text-white">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10">
              <Brain className="h-5 w-5 text-chart-2" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-white/70">Dimentry</p>
              <p className="text-xs text-white/60">Strategic Consulting & Technology</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="gap-2 border-white/30 bg-transparent text-white hover:bg-white/10"
          >
            <LogIn className="h-4 w-4" />
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative overflow-hidden bg-[var(--dl-hero)] pt-28 text-[var(--dl-hero-ink)] sm:pt-32">
        <div className="dl-grid-overlay absolute inset-0" />
        <div className="dl-hero-blob-left pointer-events-none absolute -left-24 top-16 h-80 w-80 rounded-full blur-3xl" />
        <div className="dl-hero-blob-right pointer-events-none absolute -right-24 top-12 h-96 w-96 rounded-full blur-3xl" />

        <div className="container relative z-10 mx-auto grid gap-10 px-4 pb-20 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <p className="dl-kicker dl-reveal">STRATEGY + AI EXECUTION</p>
            <h1 className="dl-display dl-reveal dl-delay-1 mt-5 text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              From Complex Operations To Confident, Measurable Growth.
            </h1>
            <p className="dl-reveal dl-delay-2 mt-6 max-w-2xl text-lg text-white/80">
              Dimentry helps enterprise teams redesign workflows, modernize technology, and ship
              AI-enabled systems that drive performance, reliability, and market speed.
            </p>

            <div className="dl-reveal dl-delay-3 mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                onClick={() => navigate('/project')}
                className="group bg-[var(--dl-accent)] px-8 py-6 text-base font-semibold text-muted-foreground hover:brightness-95"
              >
                Start Your Transformation
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/project')}
                className="border-white/30 bg-white/5 px-8 py-6 text-base text-white hover:bg-white/12"
              >
                View Case Studies
              </Button>
            </div>

            <div className="dl-reveal dl-delay-4 mt-8 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3">
                Enterprise-grade security
              </div>
              <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3">
                Delivery in 12-week cycles
              </div>
              <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3">
                Outcomes tied to KPIs
              </div>
            </div>
          </div>

          <div className="dl-reveal dl-delay-4 lg:col-span-5">
            <div className="dl-float relative rounded-2xl border border-white/20 bg-white/10 p-3 shadow-2xl backdrop-blur-sm">
              <ImageWithFallback
              src="https://images.unsplash.com/photo-1749006590639-e749e6b7d84c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDF8fHx8MTc3MTk2ODI0MHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
              alt="AI technology visualization"
                className="h-[280px] w-full rounded-xl object-cover sm:h-[360px]"
              />
              <div className="absolute -bottom-5 left-5 rounded-xl border border-white/20 bg-[var(--dl-hero)]/95 px-4 py-3 text-sm text-white shadow-xl">
                <p className="font-semibold">Delivery Engine</p>
                <p className="text-white/70">Strategy, data, and product teams moving in one rhythm.</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Core Expertise Section */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-14">
            <p className="dl-kicker dl-reveal">Capability Stack</p>
            <h2 className="dl-reveal dl-delay-1 mt-4 text-3xl sm:text-4xl">
              Full-spectrum technology services for high-stakes execution.
            </h2>
            <p className="dl-reveal dl-delay-2 mt-4 text-base text-[var(--dl-muted)] sm:text-lg">
              We combine architecture, analytics, and delivery discipline to turn ambitious roadmaps
              into durable business outcomes.
            </p>
          </div>

          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 md:grid-cols-2">
            {expertiseCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <article
                  key={card.title}
                  className={`dl-panel-card dl-reveal ${revealDelayClasses[index % revealDelayClasses.length]} rounded-2xl p-6 sm:p-7`}
                >
                  <div className="mb-5 flex items-center justify-between">
                    <span
                      className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${card.accentClass}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-[var(--dl-muted)]" />
                  </div>
                  <h3 className="text-2xl text-[var(--dl-ink)]">{card.title}</h3>
                  <p className="mt-3 text-[var(--dl-muted)]">{card.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Verticals */}
      <section className="dl-industry relative overflow-hidden py-16 text-white sm:py-20">
        <div className="dl-industry-wash pointer-events-none absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="dl-kicker dl-reveal text-chart-2">Industry Verticals</p>
            <h2 className="dl-reveal dl-delay-1 mt-4 text-3xl sm:text-4xl">
              Domain expertise that respects real-world constraints.
            </h2>
            <p className="dl-reveal dl-delay-2 mt-4 text-base text-white/75 sm:text-lg">
              Every engagement is tuned to the risk profile, data maturity, and compliance requirements
              of your sector.
            </p>
          </div>

          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {industryCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <article
                  key={card.title}
                  className={`dl-reveal ${revealDelayClasses[index % revealDelayClasses.length]} rounded-2xl border border-white/15 bg-white/8 p-6 backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1`}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${card.accentClass}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-white/60" />
                  </div>
                  <h3 className="text-2xl text-white">{card.title}</h3>
                  <p className="mt-3 text-white/75">{card.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-3xl border border-[var(--dl-border)] bg-[var(--dl-panel)] p-6 sm:p-10">
            <div className="mb-8 text-center">
              <p className="dl-kicker dl-reveal">Performance Snapshot</p>
              <h2 className="dl-reveal dl-delay-1 mt-4 text-3xl sm:text-4xl">Execution that compounds over time.</h2>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {performanceStats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`dl-panel-card dl-reveal ${revealDelayClasses[index]} rounded-2xl px-5 py-6 text-center`}
                >
                  <div className="text-4xl font-semibold text-[var(--dl-ink)] sm:text-5xl">{stat.value}</div>
                  <p className="mt-2 text-sm text-[var(--dl-muted)] sm:text-base">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20 pt-8 sm:pb-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[var(--dl-border)] bg-[var(--dl-panel)]">
            <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
              <div className="p-8 sm:p-10 lg:col-span-8">
                <p className="dl-kicker">Partnership Invitation</p>
                <h2 className="mt-4 text-3xl sm:text-4xl">Ready to build your next advantage?</h2>
                <p className="mt-4 max-w-2xl text-[var(--dl-muted)]">
                  Bring your hardest operational challenge. We will shape a focused roadmap,
                  align your teams, and deliver production-grade systems with measurable business impact.
                </p>
              </div>
              <div className="flex items-center justify-center border-t border-[var(--dl-border)] bg-[var(--dl-ink)] p-8 text-white lg:col-span-4 lg:border-l lg:border-t-0">
                <div className="w-full max-w-xs space-y-3">
                  <Button
                    size="lg"
                    onClick={() => navigate('/project')}
                    className="group w-full bg-[var(--dl-accent)] text-muted-foreground hover:brightness-95"
                  >
                    Start Your Transformation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/project')}
                    className="w-full border-white/30 bg-transparent text-white hover:bg-white/12"
                  >
                    Explore Work Examples
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--dl-border)] bg-[#ece8dc] py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center gap-2">
              <Brain className="h-6 w-6 text-chart-2" />
              <span className="text-xl font-semibold text-[var(--dl-ink)]">Dimentry</span>
            </div>
            <p className="mb-2 text-[var(--dl-muted)]">Strategic Consulting & Technology</p>
            <p className="text-sm text-[var(--dl-muted)]/90">© 2026 Dimentry. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
